import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';

import { Table, Typography } from 'antd';

import styled from 'styled-components';

import { getMoment, showDateNoTime } from '@libs/NwMoment';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import { getStatementDescription } from '@components/ContactsStatement/StatementUtils';
import VerticalDate from '@components/Dates/VerticalDate';
import CustomSwitch from '@components/Gui/CustomSwitch';
import EmptyState from '@components/Gui/EmptyState';
import Money from '@components/Gui/Money';
import { NwButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import NwIcon from '@components/Gui/NwIcon';
import PrintableLayout from '@components/Printable/PrintableLayout';
import PrintableLayoutOptionsSelector, {
    DEFAULT_PRINT_LAYOUT_OPTION,
} from '@components/Printable/PrintableLayoutOptionsSelector';
import ProjectTitle from '@components/Project/ProjectTitle';
import { adaptPayableRows } from '@components/StatementRowsCommon/adapters';
import TransactionTitle from '@components/Transactions/TransactionTitle';

import { useStatementContext } from '../StatementContext';
import { PrintableDrawerWrapper, StatementTableContainer } from '../StatementUI';
import PayableTotalColumn from './PayableTotalColumn';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const PaymentTitleContainer = styled.div`
    .payment-title {
        font-size: 0.9rem;
        font-weight: 600;
        text-transform: uppercase;
    }

    small {
        font-weight: 300;
        font-size: 0.7rem;
        text-transform: uppercase;
        line-height: 1em;
        display: block;
    }
`;

/**
 * PayablePrintable
 *
 * Printable version of the payable statement body.
 * Universal component for all contact types (model, agency, service)
 */
const PayablePrintable = ({ balances, startBalances, endBalances, transactions, onClose, statementTotals }) => {
    const { contactID, contactType, statementType, filters } = useStatementContext();

    const componentRef = useRef();
    const [showGross, setShowGross] = useState(false);
    const [showTaxes, setShowTaxes] = useState(false);
    const [showClientPaymentStatus, setShowClientPaymentStatus] = useState(true);
    const [showJobDetails, setShowJobDetails] = useState(true);
    const [showCurrentAvailableBalances, setShowCurrentAvailableBalances] = useState(true);
    const [showCurrentTotalBalances, setShowCurrentTotalBalances] = useState(false);
    const [showPeriodAvailableBalances, setShowPeriodAvailableBalances] = useState(false);
    const [showPeriodTotalBalances, setShowPeriodTotalBalances] = useState(false);
    const [showDescription, setShowDescription] = useState(true);
    const [showPayments, setShowPayments] = useState(true);
    const [showTotalLines, setShowTotalLines] = useState(false);
    const [showTitle, setShowTitle] = useState(true);
    const [layoutOption, setLayoutOption] = useState(DEFAULT_PRINT_LAYOUT_OPTION);
    const updateLayoutOption = useCallback((patch) => {
        setLayoutOption((prev) => ({ ...prev, ...patch }));
    }, []);

    const periodBalances = useMemo(() => {
        return showPeriodAvailableBalances || showPeriodTotalBalances;
    }, [showPeriodAvailableBalances, showPeriodTotalBalances]);

    const dayBeforePeriod = useMemo(() => {
        const dBP = getMoment(filters.range[0]).subtract(1, 'day');
        if (dBP.year() > 1900) {
            return showDateNoTime(dBP, true);
        }
        return null;
    }, [filters.range]);

    const lastDayOfPeriod = useMemo(() => {
        return showDateNoTime(filters.range[1], true);
    }, [filters.range]);

    // Dynamic labels based on contact type
    const supplierLabel = useMemo(() => {
        switch (contactType) {
            case 'model':
                return 'Model';
            case 'agency':
                return 'Agency';
            case 'service':
                return 'Service';
            default:
                return 'Supplier';
        }
    }, [contactType]);

    const transactionsToDisplay = useMemo(() => {
        if (transactions && transactions.length > 0) {
            let filteredTransactions = [...transactions];
            if (!showPayments) {
                filteredTransactions = filteredTransactions.filter((t) => !t.isPayment);
            }
            return adaptPayableRows(filteredTransactions);
        }
        return [];
    }, [transactions, showPayments]);

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        bodyClass: 'print-body',
        documentTitle: 'Statement',
        onAfterPrint: () => {
            onClose();
        },
    });

    const isTransactionPaid = (transaction) => {
        return transaction.Statements && transaction.Statements.some((st) => st.PaidStatus === 'Paid');
    };

    const showDescriptionOnlyColumn = showDescription && !showJobDetails;

    const currentColumnNumber = useMemo(() => {
        return (
            3 +
            Number(showTitle) +
            Number(showGross) +
            Number(showClientPaymentStatus) +
            Number(showJobDetails) +
            Number(showDescriptionOnlyColumn)
        );
    }, [showTitle, showGross, showClientPaymentStatus, showJobDetails, showDescriptionOnlyColumn]);

    const columns = [
        {
            title: 'Date',
            dataIndex: 'Date',
            key: 'Date',
            width: 70,
            render: (text, record) => {
                if (record.isPayment) {
                    return (
                        <div className="date-container">
                            <VerticalDate size="small" date={record.PaymentDate} verticalPadding="0" />
                        </div>
                    );
                }
                return (
                    <div className="date-container">
                        <VerticalDate size="small" date={record.RefDate} verticalPadding="0" />
                    </div>
                );
            },
        },
    ];

    if (showClientPaymentStatus) {
        columns.push({
            title: 'Client',
            dataIndex: 'ClientInvoiceStatus',
            key: 'ClientInvoiceStatus',
            width: 60,
            render: (text, record) => {
                if (record.isPayment) {
                    return <small className="payment-status">/</small>;
                }
                if (record.ClientInvoiceStatus.toLowerCase() === 'paid') {
                    return <small className="payment-status">PAID</small>;
                }
                if (!record.ClientID) {
                    return <small className="payment-status">/</small>;
                }
                return <small className="payment-status">NOT PAID</small>;
            },
        });
    }

    columns.push({
        title: supplierLabel,
        dataIndex: 'Payment',
        key: 'Payment',
        width: 60,
        render: (text, record) => {
            if (record.isPayment) {
                return (
                    <small className="payment-status">
                        <NwIcon icon={byPrefixAndName.fasl['money-check-dollar-pen']} />
                    </small>
                );
            }
            if (isTransactionPaid(record)) {
                return <small className="payment-status">PAID</small>;
            }
            return <></>;
        },
    });

    if (showTitle) {
        columns.push({
            title: 'Title',
            key: 'Title',
            render: (record) => {
                if (record.isPayment) {
                    return (
                        <div className="transaction-details">
                            <PaymentTitleContainer>
                                <small>Payment</small>
                                <div className="payment-title">{record.PaymentName}</div>
                            </PaymentTitleContainer>
                        </div>
                    );
                }
                return (
                    <div className="transaction-details">
                        {(contactType === 'agency' || contactType === 'service') && record.ModelID && (
                            <p>{getContactName(record.ModelID)}</p>
                        )}
                        <TransactionTitle transaction={record} target={contactType.toLowerCase()} />
                    </div>
                );
            },
        });
    }

    if (showJobDetails) {
        columns.push({
            title: 'Project',
            dataIndex: 'Project',
            key: 'Project',
            className: 'top-aligned',
            render: (text, record) => {
                if (record.isPayment) {
                    return (
                        <div className="project-details">
                            {record.PaymentNumber && (
                                <>
                                    <small>PAYMENT #</small>
                                    <Typography.Text code>{record.PaymentNumber}</Typography.Text>
                                </>
                            )}
                        </div>
                    );
                }
                return (
                    <div className="project-details">
                        {record.ServiceID && <p className="transaction-client">{getContactName(record.ServiceID)}</p>}
                        {record.Project && <ProjectTitle project={record.Project} compact pointer hideAlert />}
                        {showDescription && (
                            <small style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                                {getStatementDescription(record)}
                            </small>
                        )}
                    </div>
                );
            },
        });
    }

    if (showDescriptionOnlyColumn) {
        columns.push({
            title: 'Description',
            key: 'Description',
            className: 'top-aligned',
            render: (text, record) => {
                if (record.isPayment) {
                    return null;
                }
                return (
                    <small style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                        {getStatementDescription(record)}
                    </small>
                );
            },
        });
    }

    if (showGross) {
        columns.push({
            title: 'Job Amount',
            dataIndex: 'Numbers',
            key: 'Numbers',
            width: 120,
            align: 'right',
            render: (text, record) => {
                if (record.isPayment) {
                    return <></>;
                }
                if (record.Family === 'JobFee') {
                    return (
                        <div className="transaction-gross">
                            {(record.Quantity !== 1 || !['Flat', 'Units'].includes(record.TypeQuantity)) && (
                                <p>
                                    <Money amount={record.SingleFee} currency={record.Currency} />
                                    &nbsp;{`x ${record.Quantity} ${record.TypeQuantity}`}
                                </p>
                            )}
                            <p>
                                <Money amount={record.TotalFee} currency={record.Currency} />
                            </p>
                        </div>
                    );
                } else {
                    return <></>;
                }
            },
        });
    }

    columns.push({
        title: 'Amount',
        key: 'Amount',
        width: 120,
        align: 'right',
        render: (record) => {
            if (record.isPayment) {
                return (
                    <div className="statement-total">
                        <p>
                            <Money invert amount={record.PaymentAmount} currency={record.PaymentCurrency} />
                        </p>
                    </div>
                );
            }
            return (
                <div className="statement-total">
                    <PayableTotalColumn isItemExpanded={showTaxes} printable transaction={record} />
                </div>
            );
        },
    });

    const getRowClassName = (record) => {
        let cn = 'statement-row';
        if (isTransactionPaid(record)) {
            cn += ' statement-row-paid';
        }
        return cn;
    };

    return (
        <PrintableDrawerWrapper>
            <div className="printable-statement-header">
                <div className="printable-statement-header-left">
                    <PrintableLayoutOptionsSelector
                        layoutOption={layoutOption}
                        onLayoutOptionChange={updateLayoutOption}
                    />
                    <NwDivider dark>Line Details To Show</NwDivider>
                    <div className="switch-holder">
                        <CustomSwitch
                            size="small"
                            value={showClientPaymentStatus}
                            text="client payment status"
                            onChange={setShowClientPaymentStatus}
                        />

                        <CustomSwitch size="small" value={showGross} text="job gross" onChange={setShowGross} />
                        {contactType !== 'agency' && contactType !== 'service' && (
                            <CustomSwitch
                                size="small"
                                value={showTaxes}
                                text="taxes"
                                disabled={!showGross}
                                onChange={setShowTaxes}
                            />
                        )}
                    </div>
                    <div className="switch-holder">
                        <CustomSwitch size="small" value={showTitle} text="type and title" onChange={setShowTitle} />
                        <CustomSwitch
                            size="small"
                            value={showJobDetails}
                            text="project / job details"
                            onChange={setShowJobDetails}
                        />
                        <CustomSwitch
                            size="small"
                            value={showDescription}
                            text="descriptions"
                            onChange={setShowDescription}
                        />
                    </div>
                    <NwDivider dark>Totals / Balances</NwDivider>
                    <div className="switch-holder">
                        <CustomSwitch
                            size="small"
                            value={showCurrentAvailableBalances}
                            text="show current available balance"
                            onChange={setShowCurrentAvailableBalances}
                        />
                        <CustomSwitch
                            size="small"
                            value={showCurrentTotalBalances}
                            text="show current total balance"
                            onChange={setShowCurrentTotalBalances}
                        />
                    </div>
                    <div className="switch-holder">
                        <CustomSwitch
                            size="small"
                            value={showPeriodAvailableBalances}
                            text="show period available balance"
                            onChange={setShowPeriodAvailableBalances}
                        />
                        <CustomSwitch
                            size="small"
                            value={showPeriodTotalBalances}
                            text="show period total balance"
                            onChange={setShowPeriodTotalBalances}
                        />
                    </div>
                    <div className="switch-holder">
                        <CustomSwitch
                            size="small"
                            value={showTotalLines}
                            text="lines total at the bottom"
                            onChange={setShowTotalLines}
                        />
                    </div>
                    <NwDivider dark>Line Types</NwDivider>
                    <div className="switch-holder">
                        <CustomSwitch
                            size="small"
                            value={showPayments}
                            text={`include ${supplierLabel.toLowerCase()} payment lines`}
                            onChange={setShowPayments}
                        />
                    </div>
                </div>
                <div className="printable-statement-header-right">
                    <NwButton
                        size="big"
                        primary
                        label="Print"
                        icon={byPrefixAndName.fasl['print']}
                        onClick={handlePrint}
                        // onClick={getHtmlToPrint}
                    />
                </div>
            </div>
            <div className="printable-content-wrapper">
                <StatementTableContainer ref={componentRef}>
                    <PrintableLayout
                        showCompanyHeader={layoutOption.showCompanyHeader}
                        showCompanyFooter={layoutOption.showCompanyFooter}
                        onlyLogoInHeader={layoutOption.onlyLogoInHeader}
                    >
                        <div className="printable-top printable-top-block">
                            <div className="printable-description printable-description-flex">
                                <div className="printable-description-left">
                                    <small>Statement for: </small>
                                    <h2>{getContactName(contactID)}</h2>
                                </div>
                                <div className="printable-description-right">
                                    {dayBeforePeriod && (
                                        <>
                                            <small>period</small>
                                            <h2>
                                                {dayBeforePeriod} - {lastDayOfPeriod}
                                            </h2>
                                        </>
                                    )}
                                </div>
                            </div>
                            {showCurrentAvailableBalances && balances && (
                                <div className="printable-statement-inline-balances">
                                    <p>current available balance</p>
                                    <div className="printable-statement-inline-balance-amounts">
                                        {Object.entries(balances.availableBalances).map(([currency, amount]) => (
                                            <div className="printable-statement-inline-balance-amount" key={currency}>
                                                <Money currency={currency} amount={amount} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {showCurrentTotalBalances && balances && (
                                <div className="printable-statement-inline-balances">
                                    <p>current total balance</p>
                                    <div className="printable-statement-inline-balance-amounts">
                                        {Object.entries(balances.totalBalances).map(([currency, amount]) => (
                                            <div className="printable-statement-inline-balance-amount" key={currency}>
                                                <Money currency={currency} amount={amount} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <hr />
                            {periodBalances ? (
                                <div className="printable-statement-period-balances">
                                    {startBalances && (
                                        <div className="printable-statement-inline-balances-group">
                                            <h4>Balances at start ({dayBeforePeriod})</h4>
                                            {showPeriodTotalBalances && (
                                                <div className="printable-statement-inline-balances printable-statement-inline-balances-small">
                                                    <p>Total:</p>
                                                    {Object.entries(startBalances.Balances).map(
                                                        ([currency, amount]) => (
                                                            <div
                                                                className="printable-statement-inline-balance-amount"
                                                                key={currency}
                                                            >
                                                                <Money currency={currency} amount={amount} />
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                            {showPeriodAvailableBalances && (
                                                <div className="printable-statement-inline-balances printable-statement-inline-balances-small">
                                                    <p>Available:</p>
                                                    {Object.entries(startBalances.AvailableBalances).map(
                                                        ([currency, amount]) => (
                                                            <div
                                                                className="printable-statement-inline-balance-amount"
                                                                key={currency}
                                                            >
                                                                <Money currency={currency} amount={amount} />
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {startBalances && endBalances && (
                                        <div className="printable-statement-period-balances-arrow">
                                            <NwIcon icon={byPrefixAndName.fasl['arrow-right']} />
                                        </div>
                                    )}
                                    {endBalances && (
                                        <div className="printable-statement-inline-balances-group">
                                            <h4>Balances at end ({lastDayOfPeriod})</h4>
                                            {showPeriodTotalBalances && (
                                                <div className="printable-statement-inline-balances printable-statement-inline-balances-small">
                                                    <p>Total:</p>
                                                    {Object.entries(endBalances.Balances).map(([currency, amount]) => (
                                                        <div
                                                            className="printable-statement-inline-balance-amount"
                                                            key={currency}
                                                        >
                                                            <Money currency={currency} amount={amount} />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {showPeriodAvailableBalances && (
                                                <div className="printable-statement-inline-balances printable-statement-inline-balances-small">
                                                    <p>Available:</p>
                                                    {Object.entries(endBalances.AvailableBalances).map(
                                                        ([currency, amount]) => (
                                                            <div
                                                                className="printable-statement-inline-balance-amount"
                                                                key={currency}
                                                            >
                                                                <Money currency={currency} amount={amount} />
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : null}
                            {periodBalances && <hr />}
                        </div>
                        {transactionsToDisplay.length > 0 ? (
                            <Table
                                className="statement-table statement-table-printable"
                                columns={columns}
                                dataSource={transactionsToDisplay}
                                pagination={false}
                                rowClassName={(record) => getRowClassName(record)}
                                summary={(pageData) => {
                                    if (!showTotalLines) {
                                        return null;
                                    }
                                    return (
                                        <>
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell
                                                    colSpan={currentColumnNumber - 2}
                                                ></Table.Summary.Cell>
                                                <Table.Summary.Cell>
                                                    <strong>Total</strong>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell>
                                                    {statementTotals.map((item) => (
                                                        <Money
                                                            key={item.Currency}
                                                            style={{
                                                                display: 'block',
                                                                textAlign: 'right',
                                                            }}
                                                            dineroObj={item.Dinero}
                                                            boldIfDefaultCurrency
                                                        />
                                                    ))}
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        </>
                                    );
                                }}
                            />
                        ) : (
                            <EmptyState message="No Results" />
                        )}
                    </PrintableLayout>
                </StatementTableContainer>
            </div>
        </PrintableDrawerWrapper>
    );
};

export default PayablePrintable;
